/**
 * 🕶️ DEVICE DETECTION UTILITIES
 * ================================
 * 
 * Erkennt verschiedene Gerätetypen:
 * - Meta Quest 3 / VR Headsets
 * - Android Geräte
 * - Desktop Browser
 * 
 * Unterstützt manuelles Override für Testing
 */

// LocalStorage Key für manuelles Override
const DEVICE_OVERRIDE_KEY = 'deviceModeOverride';

/**
 * Erkennt Meta Quest / VR Headsets
 */
export const isMetaQuest = () => {
  // Manuelles Override prüfen
  const override = localStorage.getItem(DEVICE_OVERRIDE_KEY);
  if (override === 'quest') return true;
  if (override && override !== 'quest') return false;
  
  const ua = navigator.userAgent.toLowerCase();
  
  // Quest Browser hat "quest" oder "oculus" im User Agent
  if (ua.includes('quest') || ua.includes('oculus')) {
    return true;
  }
  
  // WebXR API prüfen (für VR-Unterstützung)
  if (navigator.xr) {
    // Async check - für sofortige Rückgabe false, aber setzen für nächsten Check
    navigator.xr.isSessionSupported('immersive-vr').then(supported => {
      if (supported && !localStorage.getItem(DEVICE_OVERRIDE_KEY)) {
        console.log('🕶️ VR-Headset via WebXR erkannt');
      }
    });
  }
  
  return false;
};

/**
 * Erkennt Android Geräte
 */
export const isAndroid = () => {
  // Manuelles Override prüfen
  const override = localStorage.getItem(DEVICE_OVERRIDE_KEY);
  if (override === 'android') return true;
  if (override && override !== 'android') return false;
  
  const ua = navigator.userAgent.toLowerCase();
  return ua.includes('android');
};

/**
 * Erkennt Desktop Browser
 */
export const isDesktop = () => {
  // Manuelles Override prüfen
  const override = localStorage.getItem(DEVICE_OVERRIDE_KEY);
  if (override === 'desktop') return true;
  if (override && override !== 'desktop') return false;
  
  return !isMetaQuest() && !isAndroid();
};

/**
 * Gibt aktuellen Device-Typ zurück
 */
export const getDeviceType = () => {
  if (isMetaQuest()) return 'quest';
  if (isAndroid()) return 'android';
  return 'desktop';
};

/**
 * Setzt manuelles Device-Override (für Testing)
 * @param {string} deviceType - 'quest', 'android', 'desktop' oder null (Auto)
 */
export const setDeviceOverride = (deviceType) => {
  if (deviceType === null || deviceType === 'auto') {
    localStorage.removeItem(DEVICE_OVERRIDE_KEY);
    console.log('🔄 Device-Erkennung: Automatisch');
  } else if (['quest', 'android', 'desktop'].includes(deviceType)) {
    localStorage.setItem(DEVICE_OVERRIDE_KEY, deviceType);
    console.log(`🔄 Device-Override: ${deviceType}`);
  } else {
    console.error('❌ Ungültiger Device-Type:', deviceType);
  }
  
  // Seite neu laden für Änderungen
  window.location.reload();
};

/**
 * Gibt aktuelles Override zurück (oder null)
 */
export const getDeviceOverride = () => {
  return localStorage.getItem(DEVICE_OVERRIDE_KEY) || null;
};

/**
 * Device-Info für Debugging
 */
export const getDeviceInfo = () => {
  const override = getDeviceOverride();
  const detected = override ? `${override} (Override)` : getDeviceType();
  
  return {
    type: getDeviceType(),
    override: override,
    detected: detected,
    userAgent: navigator.userAgent,
    hasWebXR: !!navigator.xr,
    screen: {
      width: window.screen.width,
      height: window.screen.height,
      ratio: window.devicePixelRatio
    }
  };
};
